import { FormFieldDefinition } from '@publishing-engine/domain/types/article';
import {
  addToRequiredJsonSchemaField,
  createConditionalBranch,
  pathToJsonSchemaPath,
  setConditionInJsonSchema
} from '../../utils/json-schema';
import _ from 'lodash';
import {
  FormFieldDefinitionWithConstraints,
  IFieldAvailableOptionsConstraint,
  IFieldConstraint
} from '@publishing-engine/domain/models/form-schema';
import { jsonSchemas } from './json-schema-from-form-field-definition.constants';

function setNestedObjectContainer(
  schema: { required: string[]; properties: Record<string, unknown> },
  key: string
) {
  if (typeof schema.properties[key] !== 'undefined') {
    throw new Error(`Nested object container already exists for key: ${key}`);
  }

  schema.properties[key] = {
    type: 'object',
    properties: {},
    required: []
  };

  schema.required.push(key);

  return schema;
}

function hasPath(jsonSchema: {}, path: string) {
  const isNestedPath = path.includes('.');
  // TODO we could make this support arbitraty nesting depth if needed. Right now we don't.
  if (isNestedPath) {
    throw new Error(
      'Only one level of nested paths is supported at the moment.'
    );
  }

  const subPathToObjectContainer = pathToJsonSchemaPath(path);
  const hasPath =
    typeof _.get(jsonSchema, subPathToObjectContainer) !== 'undefined';
  return hasPath;
}

function setInJsonSchema(jsonSchema: {}, path: string, value: unknown) {
  const segments = path.split('.');

  if (segments.length > 2) {
    // TODO we could make this support arbitraty nesting depth if needed. Right now we don't.
    throw new Error(
      'Only one level of nested paths is supported at the moment.'
    );
  }

  // ensure nested path is created with a corresponding JsonSchema object container.
  if (segments.length === 2) {
    if (!hasPath(jsonSchema, segments[0]!)) {
      // @ts-ignore
      jsonSchema = setNestedObjectContainer(jsonSchema, segments[0]);
    }
  }

  const jsonSchemaPath = pathToJsonSchemaPath(path);
  _.set(jsonSchema, jsonSchemaPath, value);
}

export function byComponentName(formFields: FormFieldDefinition[]) {
  return formFields.reduce((acc, ffd) => {
    acc[ffd.name] = ffd;
    return acc;
  }, {} as Record<FormFieldDefinition['name'], FormFieldDefinition>);
}

/**
 * @deprecated To be replaced with jsonSchemaFromFormFieldDefinitionsAndConstraints
 * Refactored version of jsonSchemaFromFormFieldDefinitions() from form-schema.ts
 * - Removes hard-coded assumptions about nested submission fields (metadata, fileSet)
 * - Introduces ability to handle nested fields from received FormFieldDefinition caseData path
 * - Removes exclusion of Custom:<> fields
 */
export function jsonSchemaFromFormFieldDefinitions(
  formFieldDefinitions: Record<
    FormFieldDefinition['name'],
    FormFieldDefinition
  >,
  hasArticleTypeParameter = true
): JSONSchema {
  // Removed assumptions about structure (e.g. fileSet, metadata) which currently is hard-coded in existing implementation.
  // Schemas for ArticleTypes / authorSubmission expect a 'type: <articleType>' property,
  // so we need to accommodate this edge case.
  const schema: any = hasArticleTypeParameter
    ? {
        type: 'object',
        properties: {
          type: {
            type: 'string'
          }
        },
        required: ['type']
      }
    : {
        type: 'object',
        properties: {},
        required: []
      };

  const rootFields: Record<FormFieldDefinition['name'], FormFieldDefinition> =
    {};

  const components = Object.values(formFieldDefinitions).filter(ffd => {
    if (['DoubleAnonymousPeerReview', 'Authors'].includes(ffd.name)) {
      rootFields[ffd.name] = ffd;
    }

    return true;
  });

  // Check if OpenAccessOnly is present
  const openAccessOnlyField = components.find(
    ffd => ffd.name === 'OpenAccessOnly'
  );
  const hasOpenAccessOnly = !!openAccessOnlyField;

  for (const formFieldDef of components) {
    const {
      params: { validations, ...rest },
      caseDataFields
    } = formFieldDef;

    const caseDataField = caseDataFields.main;

    // Skip PublishingModel field entirely when OpenAccessOnly is present
    // Both write to the same path (preference.publishingModel), so we only need one
    if (formFieldDef.name === 'PublishingModel' && hasOpenAccessOnly) {
      continue;
    }

    const schemaPart = jsonSchemas[validations.kind]({
      ...validations,
      ...rest
    });
    setInJsonSchema(schema, caseDataField, schemaPart);

    if (validations.required) {
      addToRequiredJsonSchemaField(schema, caseDataField);
    }
  }

  if (rootFields.DoubleAnonymousPeerReview) {
    setConditionInJsonSchema(schema, 'allOf', {
      if: {
        properties: {
          preference: {
            type: 'object',
            properties: {
              doubleAnonymousPeerReview: { const: true }
            }
          }
        }
      },
      then: {
        properties: {
          fileSet: {
            type: 'object',
            properties: {
              AnonymizedManuscript: {
                type: 'object'
              }
            },
            required: ['AnonymizedManuscript']
          }
        }
      }
    });
  }

  if (rootFields.Authors) {
    setConditionInJsonSchema(schema, 'allOf', {
      if: {
        properties: {
          metadata: {
            type: 'object',
            properties: {
              authors: {
                type: 'array',
                minItems: 1
              }
            }
          }
        }
      },
      then: {
        properties: {
          coauthorsAgreeToEthics: { const: true },
          coauthorsAgreeToSharing: { const: true }
        }
      }
    });
  }

  // Add DOI validation for specific DataAvailability DOI fields
  const urlFields = [
    'DataAvailabilityOpenlyAvailableUrl',
    'DataAvailabilityEmbargoUrl'
  ];

  for (const fieldName of urlFields) {
    const field = Object.values(formFieldDefinitions).find(
      ffd => ffd.name === fieldName
    );
    if (field) {
      const path = field.caseDataFields.main;
      const pathParts = path.split('.');

      // Navigate to the correct property in the schema
      if (pathParts.length === 2) {
        const [parent, child] = pathParts;
        if (
          parent &&
          child &&
          schema.properties[parent] &&
          typeof schema.properties[parent] === 'object' &&
          'properties' in schema.properties[parent] &&
          (schema.properties[parent] as any).properties[child]
        ) {
          // Add pattern validation for DOIs
          (schema.properties[parent] as any).properties[child].pattern =
            '^10\\.\\d{4,9}/[-._;()/:A-Za-z0-9]+$';
        }
      }
    }
  }

  return schema;
}

export function jsonSchemaFromFormFieldDefinitionsAndConstraints(
  formFieldDefinitions: FormFieldDefinitionWithConstraints[],
  hasArticleTypeParameter?: boolean
): JSONSchema {
  const schema: any = hasArticleTypeParameter
    ? {
        type: 'object',
        properties: {
          type: {
            type: 'string'
          }
        },
        required: ['type']
      }
    : {
        type: 'object',
        properties: {},
        required: []
      };

  const rootFields: Record<FormFieldDefinition['name'], FormFieldDefinition> =
    {};

  const components = formFieldDefinitions.filter(
    ({ formFieldDefinition: ffd }) => {
      if (
        ['DoubleAnonymousPeerReview', 'Authors', 'CopyrightType'].includes(
          ffd.name
        )
      ) {
        rootFields[ffd.name] = ffd;
      }

      return true;
    }
  );

  // Check if OpenAccessOnly is present
  const openAccessOnlyField = components.find(
    ({ formFieldDefinition: ffd }) => ffd.name === 'OpenAccessOnly'
  );
  const hasOpenAccessOnly = !!openAccessOnlyField;

  for (const { formFieldDefinition, formFieldConstraints } of components) {
    const {
      params: { validations, ...rest },
      caseDataFields
    } = formFieldDefinition;

    // Skip fields with no main data path (display-only, headings, info text)
    if (!caseDataFields?.main) {
      continue;
    }

    // Skip PublishingModel field entirely when OpenAccessOnly is present
    // Both write to the same path (preference.publishingModel), so we only need one
    if (formFieldDefinition.name === 'PublishingModel' && hasOpenAccessOnly) {
      continue;
    }

    const caseDataField = caseDataFields.main;
    const isOpposedReviewer =
      formFieldDefinition.name === 'OpposedReviewers' ||
      formFieldDefinition.name?.toLowerCase()?.includes('opposed');
    const schemaPart = jsonSchemas[validations.kind]({
      ...validations,
      ...rest,
      required: validations.required,
      ...(isOpposedReviewer ? { isOpposed: true } : {})
    });
    setInJsonSchema(schema, caseDataField, schemaPart);

    if (
      'externalFieldDependency' in rest &&
      rest.externalFieldDependency?.path
    ) {
      const { externalFieldDependency, ...others } = rest;
      const { path, value } = externalFieldDependency;

      const schemaPart = jsonSchemas[validations.kind]({
        ...validations,
        ...others
      });
      const ifClause = createConditionalBranch({}, path, { const: value });
      const thenClause = createConditionalBranch(
        {},
        caseDataFields.main,
        schemaPart,
        true
      );

      setConditionInJsonSchema(schema, 'allOf', {
        if: ifClause,
        then: thenClause
      });
    } else if (
      validations.required &&
      !(formFieldDefinition.name === 'PublishingModel' && hasOpenAccessOnly)
    ) {
      addToRequiredJsonSchemaField(schema, caseDataField);
    }

    // Contextual validation based on formFieldDefinition Constraints
    for (const formFieldConstraint of formFieldConstraints || []) {
      validations.required &&
        jsonSchemaConstraints[formFieldConstraint.kind](
          formFieldConstraint,
          caseDataField,
          schema
        );
    }
  }

  if (rootFields.DoubleAnonymousPeerReview) {
    setConditionInJsonSchema(schema, 'allOf', {
      if: {
        properties: {
          preference: {
            type: 'object',
            properties: {
              doubleAnonymousPeerReview: { const: true }
            }
          }
        }
      },
      then: {
        properties: {
          fileSet: {
            type: 'object',
            properties: {
              AnonymizedManuscript: {
                type: 'object'
              }
            },
            required: ['AnonymizedManuscript']
          }
        }
      }
    });
  }

  if (rootFields.Authors) {
    setConditionInJsonSchema(schema, 'allOf', {
      if: {
        properties: {
          metadata: {
            type: 'object',
            properties: {
              authors: {
                type: 'array',
                minItems: 1
              }
            }
          }
        }
      },
      then: {
        properties: {
          coauthorsAgreeToEthics: { const: true },
          coauthorsAgreeToSharing: { const: true }
        }
      }
    });
  }

  // Add DOI validation for specific DataAvailability DOI fields
  const urlFields = [
    'DataAvailabilityOpenlyAvailableUrl',
    'DataAvailabilityEmbargoUrl'
  ];

  for (const fieldName of urlFields) {
    const field = formFieldDefinitions.find(
      ffd => ffd.formFieldDefinition.name === fieldName
    );
    if (field) {
      const path = field.formFieldDefinition.caseDataFields.main;
      const pathParts = path.split('.');

      // Navigate to the correct property in the schema
      if (pathParts.length === 2) {
        const [parent, child] = pathParts;
        if (
          parent &&
          child &&
          schema.properties[parent] &&
          typeof schema.properties[parent] === 'object' &&
          'properties' in schema.properties[parent] &&
          (schema.properties[parent] as any).properties[child]
        ) {
          // Add pattern validation for DOIs
          (schema.properties[parent] as any).properties[child].pattern =
            '^10\\.\\d{4,9}/[-._;()/:A-Za-z0-9]+$';
        }
      }
    }
  }

  return schema;
}

export type JSONSchema = {
  type:
    | 'string'
    | 'number'
    | 'integer'
    | 'object'
    | 'array'
    | 'boolean'
    | 'null';
  properties: Record<string, JSONSchema>;
  required: string[];
};

export const jsonSchemaConstraints: Record<
  IFieldConstraint['kind'],
  (constraint: IFieldConstraint, caseDataField: string, schema: {}) => void
> = {
  availableOptions(constraint, caseDataField, schema) {
    const { cond, value } = constraint as IFieldAvailableOptionsConstraint;

    if (!cond) {
      setInJsonSchema(
        schema,
        caseDataField,
        jsonSchemas.options({ options: value })
      );
      return;
    }

    const {
      operands: [operand1, operand2]
    } = cond;

    if (typeof operand1 === 'string' && operand1.startsWith('$submission.')) {
      const clause = createConditionalBranch(
        {},
        `${operand1.replace('$submission.', '')}`,
        { const: operand2! }
      );
      const action = createConditionalBranch(
        {},
        `${caseDataField.replace('$submission.', '')}`,
        { enum: value },
        true
      );

      setConditionInJsonSchema(schema, 'allOf', {
        if: clause,
        then: action
      });
    }
  },
  visibility(_constraint, _caseDataField, _schema) {
    // TODO TBD
  }
};
